// 토스 POS 주문 API (metacityApi 미러 — 백엔드 /api/toss/* 호출)
//
// 토스는 Open API 로 테이블 지정이 불가능해서, 주문 투입 경로가 POS 안의 웹 워커뿐이다.
// 우리 서버가 WS 로 워커에 밀어주고, 워커가 order.add 를 호출한다.
// 그래서 POS 가 꺼져 있으면 주문이 안 들어간다(503). 대기열에 쌓지 않는다.
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined') return window.location.origin;
  return 'http://localhost:5000';
};
const API_URL = getApiUrl();

/**
 * 카트 → 토스 주문 포맷.
 *
 * menuId 는 토스 POS 의 상품 id(itemId)다. 우리 메뉴가 곧 POS 카탈로그라
 * menu_code 에 그대로 들어 있다.
 * 옵션은 choice id 만 있으면 된다 — 이름·가격은 POS 가 카탈로그에서 채운다.
 */
export const formatOrderForToss = (cartItems, tableCode) => {
  const items = cartItems.map((item) => {
    const menu = item.menuItem || item;
    const price = parseInt(menu.price || menu.basePrice || item.price || 0, 10) || 0;

    const optionChoices = [];
    const pushOpt = (o) => {
      if (!o || typeof o !== 'object') return;
      const id = Number(o.option_id ?? o.id);
      if (!Number.isFinite(id)) return;
      optionChoices.push({ id, quantity: Number(o.quantity || o.qty || 1) });
    };
    const so = item.selectedOptions;
    if (Array.isArray(so)) so.forEach(pushOpt);
    else if (so && typeof so === 'object') {
      Object.values(so).forEach(v => (Array.isArray(v) ? v.forEach(pushOpt) : pushOpt(v)));
    }

    return {
      menuId: Number(menu.menu_code ?? menu.item_id ?? menu.id),
      name: menu.name_ko || menu.menu_name || menu.name || '',
      price,
      quantity: item.quantity || 1,
      memo: item.memo || '',
      optionChoices,
    };
  });

  return { tableCode, items };
};

/**
 * 주문 전송. POS 가 꺼져 있으면 503, POS 가 주문을 거부하면 502 와 함께 안내 문구가 온다.
 * reviewBenefit = { name } | null — POS 로는 보내지 않고 매장 PC 카드·주문서·앱 미러에만 싣는다(메타시티와 동일).
 */
export const sendOrderToToss = async (orderData, storeId, reviewBenefit = null) => {
  try {
    const response = await fetch(`${API_URL}/api/toss/order`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        storeId,
        tableCode: orderData.tableCode,
        items: orderData.items,
        reviewBenefit,
      }),
    });
    const result = await response.json();
    if (!response.ok || result.success === false) {
      throw new Error(result.error || '토스 주문 전송 실패');
    }
    // displayOrderNo = 우리 표시 주문번호(POS·주문서·매장 PC 카드 공통). orderKey 는 내부 식별자라 손님에게 보이면 안 된다.
    return { success: true, message: '주문이 전송되었습니다.', orderNumber: result.displayOrderNo || null };
  } catch (error) {
    console.error('토스 주문 전송 오류:', error);
    return { success: false, message: error.message || '토스 주문 전송 중 오류가 발생했습니다.' };
  }
};

/** 현주문내역 조회 — 우리 DB(toss_orders) 기준. POS 에 묻지 않는다. */
export const getTossOrderInfo = async (storeId, tableCode) => {
  try {
    const response = await fetch(`${API_URL}/api/toss/order-info`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, tableCode }),
    });
    const result = await response.json();
    if (!response.ok || result.success === false) {
      return { success: false, orders: [], error: result.error };
    }
    return { success: true, orders: result.orders || [] };
  } catch (error) {
    console.error('토스 주문내역 조회 오류:', error);
    return { success: false, orders: [], error: error.message };
  }
};

/** 테이블에 활성 주문 존재 여부 (신규/추가 구분용) */
export const checkTableOrderExists = async (storeId, tableCode) => {
  try {
    const { success, orders } = await getTossOrderInfo(storeId, tableCode);
    if (!success) return { hasOrder: false };
    return { hasOrder: (orders || []).some(o => (o.items || []).length > 0) };
  } catch (error) {
    console.error('토스 테이블 주문 확인 오류:', error);
    return { hasOrder: false };
  }
};

/** 주문 직후 POS 주문번호 조회 (주문완료 화면 표시용) */
export const fetchTossTableOrderNumber = async (storeId, tableCode) => {
  try {
    const { success, orders } = await getTossOrderInfo(storeId, tableCode);
    if (!success || orders.length === 0) return { success: true, orderNumber: null };
    return { success: true, orderNumber: orders[0].orderNo || null };
  } catch (error) {
    console.error('[토스] 테이블 주문번호 조회 오류:', error);
    return { success: true, orderNumber: null };
  }
};

/** 직원호출 — 매장 PC 클라이언트(토스용) 팝업으로 간다. 다른 POS 와 같은 구조 */
export const sendStaffCallToToss = async (storeId, tableCode, reasonIds) => {
  try {
    const response = await fetch(`${API_URL}/api/toss/staff-call`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, tableCode, reasonIds }),
    });
    const result = await response.json();
    if (!response.ok || result.success === false) {
      return { success: false, error: result.error || '직원호출 실패' };
    }
    return { success: true, callItems: result.callItems || [] };
  } catch (error) {
    console.error('토스 직원호출 오류:', error);
    return { success: false, error: error.message };
  }
};
